import { useEffect, useRef } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "../../i18n";
import { buttonVariants } from "../ui/button";
import { useShowcaseMedia } from "../showcase-media-provider";

export function HomeImageReveal() {
  const images = useShowcaseMedia().revealPhotos;
  const { t } = useTranslation();
  const root = useRef<HTMLDivElement>(null);
  const title = (image: typeof images[number]) => t(image.titleKey);

  useEffect(() => {
    const track = root.current;
    const section = track?.parentElement;
    const stage = track?.querySelector<HTMLElement>(".home-reveal-stage");
    const grid = track?.querySelector<HTMLElement>(".home-reveal-tiles");
    const copy = track?.querySelector<HTMLElement>(".home-reveal-copy");
    const action = track?.querySelector<HTMLAnchorElement>(".home-reveal-action");
    const opening = copy?.querySelector<HTMLElement>('[data-phase="opening"]');
    const closing = copy?.querySelector<HTMLElement>('[data-phase="closing"]');
    if (!track || !section || !stage || !grid || !copy || !action || !opening || !closing) return;
    const tiles = Array.from(grid.querySelectorAll<HTMLAnchorElement>(".home-reveal-tile"));
    const preference = matchMedia("(prefers-reduced-motion: reduce)");
    let stop = () => {};

    function configure() {
      stop();
      if (preference.matches) return;
      track!.dataset.animated = "true";
      const clamp = (value: number) => Math.max(0, Math.min(1, value));
      const phase = (value: number, start: number, end: number) => clamp((value - start) / (end - start));
      const ease = (value: number) => value < .5 ? 4 * value ** 3 : 1 - (-2 * value + 2) ** 3 / 2;
      let frame = 0;
      let progress = 0;
      let target = 0;
      let previousTime = 0;

      const measure = () => {
        const top = Number.parseFloat(getComputedStyle(stage!).top) || 0;
        const distance = track!.offsetHeight - stage!.offsetHeight;
        target = clamp((top - track!.getBoundingClientRect().top) / Math.max(1, distance));
      };
      const render = () => {
        const width = grid!.offsetWidth;
        const height = grid!.offsetHeight;
        const viewport = stage!.clientHeight;
        const style = getComputedStyle(grid!);
        const gap = Number.parseFloat(style.gap) || 0;
        const columns = Number.parseInt(style.getPropertyValue("--reveal-columns"), 10);
        const rows = Math.ceil(tiles.length / columns);
        const centerColumn = (columns - 1) / 2;
        const tileWidth = (width - gap * (columns - 1)) / columns;
        const tileHeight = tileWidth * 9 / 16;
        const travel = (viewport + height) / 2;
        const expansion = ease(phase(progress, .37, 1));
        const scale = 1 + 1.05 * expansion;
        const reveal = phase(progress, .82, .98);
        grid!.style.transform = `scale(${scale})`;
        const textMotion = ease(phase(progress, 0, .72));
        const textSettle = ease(phase(progress, .72, 1));
        const textScale = .96 + .08 * textMotion - .04 * textSettle;
        const textY = action!.offsetHeight / 2 * (1 - reveal)
          + 24 * (1 - textMotion) - 16 * textMotion * (1 - textSettle);
        copy!.style.transform = `translate3d(0,${textY}px,0) scale(${textScale})`;
        const messageProgress = phase(progress, .64, .82);
        const openingProgress = ease(phase(messageProgress, 0, .5));
        const closingProgress = ease(phase(messageProgress, .5, 1));
        const openingOpacity = 1 - openingProgress;
        const closingOpacity = closingProgress;
        opening!.style.opacity = String(openingOpacity);
        opening!.style.visibility = openingOpacity > 0 ? "visible" : "hidden";
        opening!.style.transform = `translate3d(0,${-24 * openingProgress}px,0)`;
        closing!.style.opacity = String(closingOpacity);
        closing!.style.visibility = closingOpacity > 0 ? "visible" : "hidden";
        closing!.style.transform = `translate3d(0,${24 * (1 - closingProgress)}px,0)`;
        opening!.ariaHidden = String(messageProgress >= .5);
        closing!.ariaHidden = String(messageProgress < .5);
        section!.setAttribute("aria-labelledby", messageProgress < .5 ? opening!.id : closing!.id);
        action!.style.opacity = String(reveal);
        action!.style.transform = `translate3d(0,${12 * (1 - reveal)}px,0)`;
        action!.inert = reveal < .5;
        const bounds = stage!.getBoundingClientRect();
        tiles.forEach((tile, index) => {
          const column = index % columns;
          const row = Math.floor(index / columns);
          const direction = column % 2 === 1 ? 1 : -1;
          const order = direction === 1 ? row : rows - 1 - row;
          const stagger = order / Math.max(1, rows - 1) * .114;
          const arrival = phase(progress, .08 + stagger, .60 + stagger);
          const x = (column - centerColumn) * tileWidth * .775 * expansion;
          const rowCenter = (row - (rows - 1) / 2) * (tileHeight + gap);
          // The central columns clear vertically while the outer columns spread sideways.
          const clearance = Math.max(0, (viewport / 2 + tileHeight * scale / 2 + 24) / scale - Math.abs(rowCenter));
          const y = direction * travel * (1 - arrival)
            + (Math.abs(column - centerColumn) < 1 ? Math.sign(rowCenter) * clearance * phase(progress, .68, 1) : 0);
          tile.style.transform = `translate3d(${x}px,${y}px,0)`;
          tile.style.visibility = arrival > 0 ? "visible" : "hidden";
          const rect = tile.getBoundingClientRect();
          tile.inert = arrival < .8 || rect.bottom <= bounds.top || rect.top >= bounds.bottom
            || rect.right <= bounds.left || rect.left >= bounds.right;
        });
      };
      const update = (time: number) => {
        const elapsed = previousTime ? Math.min(64, time - previousTime) : 16;
        previousTime = time;
        progress += (target - progress) * (1 - Math.exp(-elapsed / 100));
        if (Math.abs(target - progress) < .0001) progress = target;
        render();
        frame = progress === target ? 0 : requestAnimationFrame(update);
        if (!frame) previousTime = 0;
      };
      const schedule = () => {
        measure();
        if (!frame) frame = requestAnimationFrame(update);
      };
      window.addEventListener("scroll", schedule, { passive: true });
      window.addEventListener("resize", schedule);
      const observer = new ResizeObserver(schedule);
      observer.observe(track!);
      observer.observe(stage!);
      observer.observe(grid!);
      observer.observe(action!);
      measure();
      progress = target;
      render();
      stop = () => {
        cancelAnimationFrame(frame);
        window.removeEventListener("scroll", schedule);
        window.removeEventListener("resize", schedule);
        observer.disconnect();
        [grid!, copy!, action!, opening!, closing!, ...tiles].forEach(element => {
          element.style.removeProperty("transform");
          element.style.removeProperty("opacity");
          element.style.removeProperty("visibility");
        });
        tiles.forEach(tile => { tile.inert = false; });
        action!.inert = false;
        opening!.ariaHidden = "true";
        closing!.ariaHidden = "false";
        section!.setAttribute("aria-labelledby", closing!.id);
        delete track!.dataset.animated;
      };
    }
    configure();
    preference.addEventListener("change", configure);
    return () => { preference.removeEventListener("change", configure); stop(); };
  }, []);

  return <section className={`home-section
  pt-25 [scroll-margin-top:calc(var(--site-navigation-height,79px)_+_24px)]
  maxw-[760px]:pt-18
`} id="home-images" aria-labelledby="home-image-reveal-closing-title">
    <div className={`home-image-reveal
  [&[data-animated=true]]:[height:400svh] [&[data-animated=true]]:relative
  [&[data-animated=true]_.home-reveal-stage]:sticky [&[data-animated=true]_.home-reveal-stage]:[top:var(--site-navigation-height,79px)] [&[data-animated=true]_.home-reveal-stage]:[height:calc(100svh_-_var(--site-navigation-height,79px))] [&[data-animated=true]_.home-reveal-stage]:overflow-clip [&[data-animated=true]_.home-reveal-stage]:isolate [&[data-animated=true]_.home-reveal-stage]:[background:var(--background)]
  [&[data-animated=true]_.home-reveal-content]:relative [&[data-animated=true]_.home-reveal-content]:[z-index:2] [&[data-animated=true]_.home-reveal-content]:w-full [&[data-animated=true]_.home-reveal-content]:h-full [&[data-animated=true]_.home-reveal-content]:pointer-events-none
  [&[data-animated=true]_.home-reveal-copy]:[will-change:transform] [&[data-animated=true]_.home-reveal-copy]:backface-hidden
  [&[data-animated=true]_.home-reveal-tiles]:absolute [&[data-animated=true]_.home-reveal-tiles]:[left:50%] [&[data-animated=true]_.home-reveal-tiles]:[top:50%] [&[data-animated=true]_.home-reveal-tiles]:[translate:-50%_-50%] [&[data-animated=true]_.home-reveal-tiles]:[will-change:transform]
  [&[data-animated=true]_.home-reveal-tile]:[will-change:transform]
  [&[data-animated=true]_.home-reveal-action]:pointer-events-auto [&[data-animated=true]_.home-reveal-action]:[will-change:transform,opacity]
  [&[data-animated=true]_.home-reveal-message[data-phase=opening]]:block
  [&[data-animated=true]_.home-reveal-message]:[will-change:transform,opacity] [&[data-animated=true]_.home-reveal-message]:backface-hidden
`} ref={root}>
      <div className={`home-reveal-stage
  flex flex-col items-center gap-8
`}>
        <div className={`home-reveal-content
  flex flex-col items-center justify-center gap-7 text-center
`}>
          <div className="home-reveal-copy"><h2 id="home-image-reveal-title">
            <span id="home-image-reveal-opening-title" className={`home-reveal-message
  [grid-area:1/1]
  [&[data-phase=opening]]:hidden
`} data-phase="opening" aria-hidden="true">{t("creativeHome.imageRevealOpeningTitle")}</span>
            <span id="home-image-reveal-closing-title" className={`home-reveal-message
  [grid-area:1/1]
  [&[data-phase=opening]]:hidden
`} data-phase="closing" aria-hidden="false">{t("creativeHome.imageRevealTitle")}</span>
          </h2></div>
          <Link to="/explore" search={{ modality: "image" }} className={buttonVariants({ variant: "link", className: "home-reveal-action" })}>{t("creativeHome.exploreImage")}</Link>
        </div>
        <div className={`home-reveal-tiles
  grid [--reveal-columns:6] [grid-template-columns:repeat(var(--reveal-columns),minmax(0,1fr))] gap-6 w-full
  maxw-[700px]:[--reveal-columns:3] maxw-[700px]:gap-3
`}>{images.map(image => <a key={image.id} className={`home-reveal-tile
  block min-w-0 [aspect-ratio:16/9] [border-radius:16px] overflow-hidden [background:var(--muted)] outline-offset-1 backface-hidden
  [&:focus-visible]:[outline:2px_solid_var(--ring)]
  [&_img]:block [&_img]:w-full [&_img]:h-full [&_img]:object-cover [&_img]:object-center
`}
          href={image.src} target="_blank" rel="noopener noreferrer"
          aria-label={t("creativeHome.viewImage", { title: title(image) })}>
          <img src={image.src} width={image.width} height={image.height}
            loading="lazy" decoding="async" alt={title(image)} />
        </a>)}</div>
      </div>
    </div>
  </section>;
}
